import { useState, useMemo, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Progress } from "../components/ui/progress";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { ScrollArea } from "../components/ui/scroll-area";
import { Switch } from "../components/ui/switch";
import { Label } from "../components/ui/label";
import { Input } from "../components/ui/input";
import { AreaChart, Area, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";
import { cn } from "../lib/utils";
import { useLanguage } from "../components/LanguageProvider";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragOverlay,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  rectSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

const analyticsData = [
  { name: "Jan", entities: 4000, relationships: 2400 },
  { name: "Feb", entities: 3000, relationships: 1398 },
  { name: "Mar", entities: 2000, relationships: 9800 },
  { name: "Apr", entities: 2780, relationships: 3908 },
  { name: "May", entities: 1890, relationships: 4800 },
  { name: "Jun", entities: 2390, relationships: 3800 },
  { name: "Jul", entities: 3490, relationships: 4300 },
];

const pieData = [
  { name: "Person", value: 400, color: "hsl(var(--chart-1))" },
  { name: "Organization", value: 300, color: "hsl(var(--chart-2))" },
  { name: "Location", value: 200, color: "hsl(var(--chart-3))" },
  { name: "Event", value: 150, color: "hsl(var(--chart-4))" },
  { name: "Document", value: 100, color: "hsl(var(--chart-5))" },
];

const recentActivities = [
  { id: 1, action: "Entity created", entity: "John Smith", time: "2 min ago", icon: "fa-user-plus", color: "text-success" },
  { id: 2, action: "Relationship linked", entity: "ACME Corp → Project Alpha", time: "5 min ago", icon: "fa-link", color: "text-primary" },
  { id: 3, action: "Alert triggered", entity: "Suspicious pattern detected", time: "12 min ago", icon: "fa-triangle-exclamation", color: "text-destructive" },
  { id: 4, action: "Report generated", entity: "Weekly Analysis Q4", time: "25 min ago", icon: "fa-file-lines", color: "text-info" },
];

const projects = [
  { id: 1, name: "Operation Insight", status: "active", progress: 78 },
  { id: 2, name: "Market Analysis Q4", status: "active", progress: 45 },
  { id: 3, name: "Network Mapping", status: "paused", progress: 92 },
];

// KPI Card Widget
const KPICardWidget = ({ title, value, change, changeType, icon }) => (
  <div className="h-full">
    <CardContent className="p-4 sm:p-6 h-full">
      <div className="flex items-start justify-between">
        <div className="space-y-1">
          <p className="text-xs sm:text-sm font-medium text-muted-foreground">{title}</p>
          <div className="flex items-baseline gap-2 flex-wrap">
            <h3 className="text-xl sm:text-2xl lg:text-3xl font-bold">{value}</h3>
            <Badge variant="outline" className={cn("text-xs", changeType === "positive" ? "bg-success/10 text-success border-success/30" : changeType === "negative" ? "bg-destructive/10 text-destructive border-destructive/30" : "bg-muted")}>
              <i className={cn("fa-solid mr-1 text-[10px]", changeType === "positive" ? "fa-arrow-up" : changeType === "negative" ? "fa-arrow-down" : "fa-minus")}></i>
              {change}
            </Badge>
          </div>
        </div>
        <div className="flex h-10 w-10 sm:h-12 sm:w-12 items-center justify-center rounded-xl bg-primary/10">
          <i className={cn(icon, "text-lg sm:text-xl text-primary")}></i>
        </div>
      </div>
    </CardContent>
  </div>
);

// Area Chart Widget
const AreaChartWidget = ({ t }) => (
  <div className="h-full flex flex-col">
    <CardHeader className="pb-2 p-4 sm:p-6">
      <CardTitle className="text-sm sm:text-base">{t("dataGrowthTrends")}</CardTitle>
      <CardDescription className="text-xs hidden sm:block">{t("entitiesAndRelationships")}</CardDescription>
    </CardHeader>
    <CardContent className="p-2 sm:p-6 pt-0 flex-1">
      <div className="h-48 sm:h-64">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={analyticsData}>
            <defs>
              <linearGradient id="colorEntities" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="hsl(var(--chart-1))" stopOpacity={0.3}/><stop offset="95%" stopColor="hsl(var(--chart-1))" stopOpacity={0}/></linearGradient>
              <linearGradient id="colorRelations" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="hsl(var(--chart-2))" stopOpacity={0.3}/><stop offset="95%" stopColor="hsl(var(--chart-2))" stopOpacity={0}/></linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
            <XAxis dataKey="name" stroke="hsl(var(--muted-foreground))" fontSize={10} />
            <YAxis stroke="hsl(var(--muted-foreground))" fontSize={10} />
            <Tooltip contentStyle={{ backgroundColor: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: "var(--radius)", fontSize: "12px" }}/>
            <Area type="monotone" dataKey="entities" stroke="hsl(var(--chart-1))" fillOpacity={1} fill="url(#colorEntities)" strokeWidth={2} />
            <Area type="monotone" dataKey="relationships" stroke="hsl(var(--chart-2))" fillOpacity={1} fill="url(#colorRelations)" strokeWidth={2} />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </CardContent>
  </div>
);

// Pie Chart Widget
const PieChartWidget = ({ t }) => (
  <div className="h-full flex flex-col">
    <CardHeader className="pb-2 p-4 sm:p-6">
      <CardTitle className="text-sm sm:text-base">{t("entityDistribution")}</CardTitle>
      <CardDescription className="text-xs hidden sm:block">{t("breakdownByType")}</CardDescription>
    </CardHeader>
    <CardContent className="p-2 sm:p-6 pt-0 flex-1">
      <div className="h-40 sm:h-52">
        <ResponsiveContainer width="100%" height="100%">
          <PieChart><Pie data={pieData} cx="50%" cy="50%" innerRadius={40} outerRadius={70} paddingAngle={2} dataKey="value">{pieData.map((entry, index) => (<Cell key={`cell-${index}`} fill={entry.color} />))}</Pie><Tooltip contentStyle={{ backgroundColor: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: "var(--radius)", fontSize: "12px" }}/></PieChart>
        </ResponsiveContainer>
      </div>
      <div className="mt-2 space-y-1">{pieData.slice(0, 3).map((item, index) => (<div key={index} className="flex items-center justify-between text-xs"><div className="flex items-center gap-2"><div className="h-2 w-2 rounded-full" style={{ backgroundColor: item.color }}></div><span className="text-muted-foreground">{item.name}</span></div><span className="font-medium">{item.value}</span></div>))}</div>
    </CardContent>
  </div>
);

// Activity Widget
const ActivityWidget = ({ t }) => (
  <div className="h-full flex flex-col">
    <CardHeader className="flex flex-row items-center justify-between p-4 sm:p-6 pb-2">
      <div><CardTitle className="text-sm sm:text-base">{t("recentActivity")}</CardTitle><CardDescription className="text-xs hidden sm:block">{t("latestActions")}</CardDescription></div>
      <Button variant="ghost" size="sm" className="text-xs">{t("viewAll")}</Button>
    </CardHeader>
    <CardContent className="p-2 sm:p-6 pt-0 flex-1">
      <ScrollArea className="h-48 sm:h-64">
        <div className="space-y-2">{recentActivities.map((activity) => (<div key={activity.id} className="flex items-start gap-3 p-2 rounded-lg hover:bg-muted/50 transition-colors"><div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-muted"><i className={cn("fa-solid text-xs", activity.icon, activity.color)}></i></div><div className="flex-1 min-w-0"><p className="text-xs sm:text-sm font-medium truncate">{activity.action}</p><p className="text-xs text-muted-foreground truncate">{activity.entity}</p></div><span className="text-[10px] text-muted-foreground shrink-0">{activity.time}</span></div>))}</div>
      </ScrollArea>
    </CardContent>
  </div>
);

// Projects Widget
const ProjectsWidget = ({ t }) => (
  <div className="h-full flex flex-col">
    <CardHeader className="flex flex-row items-center justify-between p-4 sm:p-6 pb-2">
      <div><CardTitle className="text-sm sm:text-base">{t("activeProjects")}</CardTitle><CardDescription className="text-xs hidden sm:block">{t("yourInvestigations")}</CardDescription></div>
      <Button variant="outline" size="sm" className="text-xs"><i className="fa-solid fa-plus mr-1"></i><span className="hidden sm:inline">{t("new")}</span></Button>
    </CardHeader>
    <CardContent className="p-2 sm:p-6 pt-0 flex-1">
      <ScrollArea className="h-48 sm:h-64">
        <div className="space-y-3">{projects.map((project) => (<div key={project.id} className="p-3 rounded-lg border border-border hover:border-primary/50 transition-colors cursor-pointer"><div className="flex items-center justify-between mb-2"><h4 className="font-medium text-sm truncate">{project.name}</h4><Badge variant="outline" className={cn("text-[10px]", project.status === "active" && "bg-success/10 text-success border-success/30", project.status === "paused" && "bg-warning/10 text-warning border-warning/30")}>{project.status}</Badge></div><Progress value={project.progress} className="h-1.5" /><p className="text-xs text-muted-foreground mt-1">{project.progress}%</p></div>))}</div>
      </ScrollArea>
    </CardContent>
  </div>
);

// Search Widget Component - displays saved search forms from Search page with form fields
const SearchFormWidget = ({ t, config, onRemove }) => {
  const navigate = useNavigate();
  const [formValues, setFormValues] = useState({});
  
  const entityIcons = {
    person: "fa-user",
    organization: "fa-building",
    location: "fa-location-dot",
    event: "fa-calendar",
    document: "fa-file-lines"
  };
  const entityColors = {
    person: "hsl(var(--chart-1))",
    organization: "hsl(var(--chart-2))",
    location: "hsl(var(--chart-5))",
    event: "hsl(var(--chart-4))",
    document: "hsl(var(--chart-3))"
  };

  // Get first 3-4 fields for compact display
  const displayFields = (config.fields || []).slice(0, 4);

  const handleSearch = () => {
    // Build search params from form values
    const params = new URLSearchParams();
    params.set('type', config.entityType);
    params.set('view', 'results');
    Object.entries(formValues).forEach(([key, value]) => {
      if (value) params.set(key, value);
    });
    navigate(`/search?${params.toString()}`);
  };

  const handleFieldChange = (field, value) => {
    setFormValues(prev => ({ ...prev, [field]: value }));
  };

  return (
    <div className="h-full flex flex-col">
      <CardHeader className="flex flex-row items-center justify-between p-3 sm:p-4 pb-2">
        <div className="flex items-center gap-2">
          <div 
            className="flex h-7 w-7 items-center justify-center rounded-lg" 
            style={{ backgroundColor: `${entityColors[config.entityType]}20` }}
          >
            <i className={cn("fa-solid text-xs", entityIcons[config.entityType])} style={{ color: entityColors[config.entityType] }}></i>
          </div>
          <div>
            <CardTitle className="text-xs sm:text-sm capitalize">{config.entityType} {t("search")}</CardTitle>
          </div>
        </div>
        <Button variant="ghost" size="icon" className="h-6 w-6" onClick={onRemove}>
          <i className="fa-solid fa-xmark text-xs"></i>
        </Button>
      </CardHeader>
      <CardContent className="p-3 sm:p-4 pt-0 flex-1 flex flex-col">
        <div className="space-y-2 flex-1">
          {displayFields.map((field, idx) => (
            <div key={idx} className="space-y-1">
              <Label className="text-xs capitalize text-muted-foreground">{field.replace(/([A-Z])/g, ' $1').trim()}</Label>
              <Input 
                placeholder={`${t("enter") || "Enter"} ${field}...`}
                className="h-7 text-xs"
                value={formValues[field] || ''}
                onChange={(e) => handleFieldChange(field, e.target.value)}
              />
            </div>
          ))}
          {config.fields?.length > 4 && (
            <p className="text-[10px] text-muted-foreground text-center">
              +{config.fields.length - 4} {t("moreFields") || "more fields"}
            </p>
          )}
        </div>
        <Button className="w-full h-8 mt-3" size="sm" onClick={handleSearch}>
          <i className="fa-solid fa-magnifying-glass mr-2 text-xs"></i>
          {t("search")}
        </Button>
      </CardContent>
    </div>
  );
};

// Widget config for rendering
const widgetComponents = {
  kpi1: () => <KPICardWidget title="Total Entities" value="124.5K" change="+12.5%" changeType="positive" icon="fa-solid fa-database" />,
  kpi2: () => <KPICardWidget title="Relationships" value="892.3K" change="+8.2%" changeType="positive" icon="fa-solid fa-link" />,
  kpi3: () => <KPICardWidget title="Active Queries" value="2,847" change="-3.1%" changeType="negative" icon="fa-solid fa-magnifying-glass-chart" />,
  kpi4: () => <KPICardWidget title="Alerts Today" value="47" change="+24" changeType="neutral" icon="fa-solid fa-bell" />,
  areaChart: AreaChartWidget,
  pieChart: PieChartWidget,
  activity: ActivityWidget,
  projects: ProjectsWidget,
};

// Sortable Widget Component
const SortableWidget = ({ id, editMode, className, children }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id, disabled: !editMode });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 1000 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cn(className, "relative group")}
    >
      <Card className={cn(
        "h-full overflow-hidden transition-all",
        editMode && "ring-2 ring-dashed ring-primary/30 hover:ring-primary/50",
        isDragging && "ring-primary shadow-lg"
      )}>
        {editMode && (
          <div
            {...attributes}
            {...listeners}
            className="absolute top-2 right-2 z-10 flex h-8 w-8 cursor-grab items-center justify-center rounded-md bg-primary/10 text-primary opacity-0 group-hover:opacity-100 transition-opacity hover:bg-primary/20"
          >
            <i className="fa-solid fa-grip-vertical text-sm"></i>
          </div>
        )}
        {children}
      </Card>
    </div>
  );
};

// Widget Overlay (shown while dragging)
const WidgetOverlay = ({ id }) => {
  const Component = widgetComponents[id];
  if (!Component) return null;
  
  return (
    <Card className="ring-2 ring-primary shadow-2xl opacity-90 overflow-hidden">
      <Component />
    </Card>
  );
};

export default function Dashboard() {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();
  const [timeRange, setTimeRange] = useState("7d");
  const [editMode, setEditMode] = useState(false);
  const [activeId, setActiveId] = useState(null);
  const [savedWidgets, setSavedWidgets] = useState([]);
  
  // Widget order state - persists reordering
  const [kpiOrder, setKpiOrder] = useState(["kpi1", "kpi2", "kpi3", "kpi4"]);
  const [chartsOrder, setChartsOrder] = useState(["areaChart", "pieChart"]);
  const [bottomOrder, setBottomOrder] = useState(["activity", "projects"]);

  // Load saved widgets from sessionStorage on mount, route change, and when page becomes visible
  useEffect(() => {
    const loadWidgets = () => {
      const loadedWidgets = sessionStorage.getItem('dashboardWidgets');
      if (loadedWidgets) {
        try {
          const parsed = JSON.parse(loadedWidgets);
          setSavedWidgets(parsed);
        } catch (e) {
          console.error('Error loading saved widgets:', e);
        }
      }
    };

    // Load on mount
    loadWidgets();

    // Also load when page becomes visible (e.g., user navigates back)
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        loadWidgets();
      }
    };

    // Also load on focus
    const handleFocus = () => loadWidgets();

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleFocus);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleFocus);
    };
  }, [location.pathname]); // Re-run when route changes

  // Remove a saved widget
  const handleRemoveWidget = (index) => {
    const newWidgets = savedWidgets.filter((_, i) => i !== index);
    setSavedWidgets(newWidgets);
    sessionStorage.setItem('dashboardWidgets', JSON.stringify(newWidgets));
  };

  // Translated widget components
  const widgetComponents = {
    kpi1: () => <KPICardWidget title={t("totalEntities")} value="124.5K" change="+12.5%" changeType="positive" icon="fa-solid fa-database" />,
    kpi2: () => <KPICardWidget title={t("relationships")} value="892.3K" change="+8.2%" changeType="positive" icon="fa-solid fa-link" />,
    kpi3: () => <KPICardWidget title={t("activeQueries")} value="2,847" change="-3.1%" changeType="negative" icon="fa-solid fa-magnifying-glass-chart" />,
    kpi4: () => <KPICardWidget title={t("alertsToday")} value="47" change="+24" changeType="neutral" icon="fa-solid fa-bell" />,
    areaChart: () => <AreaChartWidget t={t} />,
    pieChart: () => <PieChartWidget t={t} />,
    activity: () => <ActivityWidget t={t} />,
    projects: () => <ProjectsWidget t={t} />,
  };

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const handleDragStart = (event) => {
    setActiveId(event.active.id);
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    setActiveId(null);

    if (!over || active.id === over.id) return;

    // Find which section the items belong to
    if (kpiOrder.includes(active.id) && kpiOrder.includes(over.id)) {
      const oldIndex = kpiOrder.indexOf(active.id);
      const newIndex = kpiOrder.indexOf(over.id);
      setKpiOrder(arrayMove(kpiOrder, oldIndex, newIndex));
    } else if (chartsOrder.includes(active.id) && chartsOrder.includes(over.id)) {
      const oldIndex = chartsOrder.indexOf(active.id);
      const newIndex = chartsOrder.indexOf(over.id);
      setChartsOrder(arrayMove(chartsOrder, oldIndex, newIndex));
    } else if (bottomOrder.includes(active.id) && bottomOrder.includes(over.id)) {
      const oldIndex = bottomOrder.indexOf(active.id);
      const newIndex = bottomOrder.indexOf(over.id);
      setBottomOrder(arrayMove(bottomOrder, oldIndex, newIndex));
    }
  };

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
    >
      <div className="p-3 sm:p-4 lg:p-6 space-y-4 sm:space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h2 className="text-xl sm:text-2xl font-bold">{t("analyticsOverview")}</h2>
            <p className="text-sm text-muted-foreground hidden sm:block">{t("realTimeInsights")}</p>
          </div>
          <div className="flex items-center gap-2 sm:gap-3 flex-wrap">
            <div className="flex items-center gap-2">
              <Label htmlFor="edit-mode" className="text-xs sm:text-sm">{t("edit")}</Label>
              <Switch id="edit-mode" data-testid="edit-mode-toggle" checked={editMode} onCheckedChange={setEditMode} />
            </div>
            <Select value={timeRange} onValueChange={setTimeRange}>
              <SelectTrigger className="w-28 sm:w-36 h-8 sm:h-9 text-xs sm:text-sm"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="24h">{t("lastDay")}</SelectItem>
                <SelectItem value="7d">{t("lastWeek")}</SelectItem>
                <SelectItem value="30d">{t("lastMonth")}</SelectItem>
              </SelectContent>
            </Select>
            <Button variant="outline" size="sm" className="h-8 sm:h-9"><i className="fa-solid fa-download mr-2"></i><span className="hidden sm:inline">{t("export")}</span></Button>
          </div>
        </div>

        {editMode && (
          <div className="flex items-center gap-2 p-3 rounded-lg bg-primary/10 border border-primary/30">
            <i className="fa-solid fa-info-circle text-primary"></i>
            <span className="text-sm text-primary">{t("editModeEnabled")}</span>
          </div>
        )}

        {/* KPI Cards - Sortable */}
        <SortableContext items={kpiOrder} strategy={rectSortingStrategy}>
          <div className="grid gap-3 sm:gap-4 grid-cols-2 lg:grid-cols-4">
            {kpiOrder.map((id) => {
              const Component = widgetComponents[id];
              return (
                <SortableWidget key={id} id={id} editMode={editMode}>
                  <Component />
                </SortableWidget>
              );
            })}
          </div>
        </SortableContext>

        {/* Charts - Sortable */}
        <SortableContext items={chartsOrder} strategy={rectSortingStrategy}>
          <div className="grid gap-4 lg:grid-cols-3">
            {chartsOrder.map((id) => {
              const Component = widgetComponents[id];
              return (
                <SortableWidget 
                  key={id} 
                  id={id} 
                  editMode={editMode}
                  className={id === "areaChart" ? "lg:col-span-2" : ""}
                >
                  <Component />
                </SortableWidget>
              );
            })}
          </div>
        </SortableContext>

        {/* Bottom Section - Sortable */}
        <SortableContext items={bottomOrder} strategy={rectSortingStrategy}>
          <div className="grid gap-4 lg:grid-cols-2">
            {bottomOrder.map((id) => {
              const Component = widgetComponents[id];
              return (
                <SortableWidget key={id} id={id} editMode={editMode}>
                  <Component />
                </SortableWidget>
              );
            })}
          </div>
        </SortableContext>

        {/* Saved Search Widgets */}
        {savedWidgets.length > 0 && (
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-medium flex items-center gap-2">
                <i className="fa-solid fa-bookmark text-primary"></i>
                {t("savedSearches") || "Saved Searches"}
              </h3>
              <Badge variant="secondary">{savedWidgets.length}</Badge>
            </div>
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {savedWidgets.map((widget, index) => (
                <Card key={`saved-widget-${index}`} className="h-full overflow-hidden">
                  <SearchFormWidget 
                    t={t}
                    config={widget}
                    onRemove={() => handleRemoveWidget(index)}
                  />
                </Card>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Drag Overlay */}
      <DragOverlay>
        {activeId ? <WidgetOverlay id={activeId} /> : null}
      </DragOverlay>
    </DndContext>
  );
}
